import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { useSnackbar } from 'notistack';
import { playlistCustomLimit } from 'src/config';
import { useUserState } from 'src/contexts/UserContext';
import PlaylistsApi, { type PlaylistContentType } from './api';
import { usePlaylists } from './usePlaylists';
import { usePlaylistMutations } from './usePlaylistMutations';
import { usePlaylistStatus } from './usePlaylistStatus';

const menuPaperSx = {
  minWidth: 280,
  maxWidth: 'min(320px, calc(100vw - 48px))',
  maxHeight: 'min(70vh, 420px)',
  overflow: 'auto' as const,
};

export function PlaylistActionsMenu(props: {
  contentId: string;
  contentType: PlaylistContentType;
  variant?: 'detail' | 'listing';
}) {
  if (props.contentType === 'challenge') {
    return null;
  }
  return <PlaylistActionsMenuInner {...props} />;
}

function PlaylistActionsMenuInner({
  contentId,
  contentType,
  variant = 'detail',
}: {
  contentId: string;
  contentType: PlaylistContentType;
  variant?: 'detail' | 'listing';
}) {
  const { isLoggedIn } = useUserState();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [newName, setNewName] = useState('');
  const open = Boolean(anchor);
  const { data: playlists } = usePlaylists(isLoggedIn);
  const watchLaterId = playlists?.find((p) => p.playlistType === 'watch_later')?.id;
  const { data: status, isLoading: statusLoading } = usePlaylistStatus(
    contentId,
    contentType,
    isLoggedIn && open,
  );
  const { toggleWatchLater, addItem, removeItem, createPlaylist } =
    usePlaylistMutations();

  const [watchLaterLocal, setWatchLaterLocal] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    setWatchLaterLocal(undefined);
  }, [contentId, contentType, status?.inWatchLater]);

  const serverInWatchLater = status?.inWatchLater ?? false;
  const watchLaterEffective = watchLaterLocal ?? serverInWatchLater;
  const toggleWatchBusy = toggleWatchLater.isLoading;

  const custom = useMemo(
    () => playlists?.filter((p) => p.playlistType === 'custom') ?? [],
    [playlists],
  );

  const atLimit = custom.length >= playlistCustomLimit;

  const togglePlaylistMembership = useCallback(
    async (playlistId: string, name: string, checked: boolean) => {
      if (checked) {
        await addItem.mutateAsync({ playlistId, contentId, contentType });
        enqueueSnackbar(`Dodano do: ${name}`, { variant: 'success' });
      } else {
        const d = await PlaylistsApi.getOne(playlistId);
        const hit = d.items.find(
          (it) =>
            it.contentId === contentId && it.contentType === contentType,
        );
        if (hit) {
          await removeItem.mutateAsync({
            playlistId,
            itemId: hit.id,
            contentId,
            contentType,
          });
          enqueueSnackbar(`Usunięto z: ${name}`, { variant: 'success' });
        }
      }
    },
    [addItem, removeItem, contentId, contentType, enqueueSnackbar],
  );

  const createInline = useCallback(async () => {
    const name = newName.trim();
    if (!name || atLimit) return;
    const row = await createPlaylist.mutateAsync({
      name,
      isPrivate: true,
    });
    await addItem.mutateAsync({
      playlistId: row.id,
      contentId,
      contentType,
    });
    enqueueSnackbar(`Dodano do: ${name}`, { variant: 'success' });
    setNewName('');
    setAnchor(null);
  }, [
    newName,
    atLimit,
    createPlaylist,
    addItem,
    contentId,
    contentType,
    enqueueSnackbar,
  ]);

  const onWatchLaterToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!watchLaterId || statusLoading) return;
      const next = !watchLaterEffective;
      setWatchLaterLocal(next);
      toggleWatchLater.mutate(
        {
          playlistId: watchLaterId,
          contentId,
          contentType,
          shouldAdd: next,
          itemId: status?.watchLaterItemId,
        },
        {
          onSuccess: () => {
            enqueueSnackbar(
              next ? 'Dodano do: Na później' : 'Usunięto z: Na później',
              { variant: 'success' },
            );
            setWatchLaterLocal(undefined);
          },
          onError: () => setWatchLaterLocal(undefined),
        },
      );
    },
    [
      watchLaterId,
      statusLoading,
      watchLaterEffective,
      toggleWatchLater,
      contentId,
      contentType,
      status?.watchLaterItemId,
      enqueueSnackbar,
    ],
  );

  const onTriggerClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isLoggedIn) {
        history.push('/login');
        return;
      }
      setAnchor(e.currentTarget);
    },
    [isLoggedIn, history],
  );

  const inPlaylist = useCallback(
    (id: string) => status?.playlistIds.includes(id) ?? false,
    [status?.playlistIds],
  );

  const listingChipSx = {
    height: 28,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: 'none',
    '&:hover': { backgroundColor: 'action.hover' },
    '& .MuiChip-label': {
      px: 0,
      minWidth: 0,
      width: 0,
      overflow: 'hidden',
    },
    '& .MuiChip-icon': { ml: '4px', mr: '4px', my: 0 },
  };

  const trigger =
    variant === 'detail' ? (
      <Tooltip title="Dodaj do playlisty">
        <IconButton
          size="small"
          onClick={onTriggerClick}
          aria-label="Dodaj do playlisty"
        >
          <PlaylistAddIcon />
        </IconButton>
      </Tooltip>
    ) : (
      <Chip
        clickable
        component="button"
        type="button"
        variant="filled"
        color="default"
        size="small"
        icon={<PlaylistAddIcon fontSize="small" sx={{ fontSize: 18 }} />}
        label=""
        aria-label="Dodaj do playlisty"
        onClick={onTriggerClick}
        sx={listingChipSx}
      />
    );

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: menuPaperSx }}
      >
        <MenuItem
          dense
          disabled={!watchLaterId || toggleWatchBusy}
          onClick={onWatchLaterToggle}
        >
          <ListItemIcon>
            {watchLaterEffective ? (
              <BookmarkIcon fontSize="small" />
            ) : (
              <BookmarkBorderIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText primary="Na później" />
          <Checkbox
            edge="end"
            checked={watchLaterEffective}
            size="small"
            tabIndex={-1}
            disableRipple
            sx={{ pointerEvents: 'none' }}
          />
        </MenuItem>
        <Divider />
        {custom.map((p) => (
          <MenuItem
            key={p.id}
            dense
            onClick={(e) => {
              e.stopPropagation();
              void togglePlaylistMembership(p.id, p.name, !inPlaylist(p.id));
            }}
          >
            <Checkbox
              checked={inPlaylist(p.id)}
              size="small"
              tabIndex={-1}
              disableRipple
              sx={{ pointerEvents: 'none' }}
            />
            <ListItemText primary={p.name} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          component="div"
          disableRipple
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            height: 'auto',
            minHeight: 0,
            whiteSpace: 'normal',
            py: 1.5,
            px: 2,
          }}
        >
          <Stack spacing={1} sx={{ width: 1 }}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Nowa playlista"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={atLimit}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (
                  e.key === 'Enter' &&
                  newName.trim() &&
                  !atLimit &&
                  !createPlaylist.isLoading
                ) {
                  e.preventDefault();
                  void createInline();
                }
              }}
            />
            <Button
              size="small"
              fullWidth
              variant="contained"
              disabled={
                !newName.trim() || atLimit || createPlaylist.isLoading
              }
              onClick={() => void createInline()}
            >
              Utwórz i dodaj
            </Button>
            {atLimit ? (
              <Typography variant="caption" color="text.secondary">
                {`Limit ${playlistCustomLimit} playlist`}
              </Typography>
            ) : null}
          </Stack>
        </MenuItem>
      </Menu>
    </>
  );
}
